import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart } from 'react-minimal-pie-chart';
import { applyTypography, BaseButton, Modal } from '@repo/common';

const CORE_ITEMS = [
  { code: 'C1', label: 'Goals & Direction', key: 'c1Score' },
  { code: 'C2', label: 'Korean Background', key: 'c2Score' },
  { code: 'C3', label: 'Work Experience', key: 'c3Score' },
  { code: 'C4', label: 'Skills & Portfolio', key: 'c4Score' },
  { code: 'C5', label: 'Language Ability', key: 'c5Score' },
];

const OVERLAY_ITEMS = [
  { code: 'O1', label: 'Visa Readiness', key: 'o1Score' },
  { code: 'O2', label: 'Financial Readiness', key: 'o2Score' },
  { code: 'O3', label: 'Academic Fit', key: 'o3Score' },
  { code: 'O4', label: 'Career Fit', key: 'o4Score' },
  { code: 'O5', label: 'Cultural Readiness', key: 'o5Score' },
  { code: 'O6', label: 'Network & Support', key: 'o6Score' },
];

// ─── 점수 표시 헬퍼 ─────────────────────────────────────────────────────────
const formatScore100 = (value) =>
  value != null && Number.isFinite(Number(value))
    ? Number(value).toFixed(1)
    : '–';

// ─── 하단 액션 버튼 (Go Home / Retake) ──────────────────────────────────────
// Retake Assessment: 점수·선택지를 초기화하고 설문을 처음부터 다시 진행한다.
function ResultActions({ onRetake }) {
  const navigate = useNavigate();
  const [isRetakeModalOpen, setIsRetakeModalOpen] = useState(false);

  const handleConfirmRetake = () => {
    setIsRetakeModalOpen(false);

    if (onRetake) {
      onRetake();
      return;
    }
    // onRetake 미전달 시(예외) 설문 시작 라우트로 이동
    navigate('/pathfinder/onboarding');
  };

  return (
    <div className='flex items-center w-full gap-6'>
      <div className='flex-1 min-w-0'>
        <BaseButton
          type='text_basic'
          size='xl'
          hideIcon
          width='100%'
          onClick={() => setIsRetakeModalOpen(true)}
        >
          Retake Pathfinder
        </BaseButton>
      </div>
      <div className='flex-1 min-w-0'>
        <BaseButton
          type='primary'
          size='xl'
          hideIcon
          width='100%'
          onClick={() => navigate('/pathfinder/dashboard')}
        >
          Go to Dashboard
        </BaseButton>
      </div>

      <Modal
        isOpen={isRetakeModalOpen}
        type='warning'
        title='Retake Pathfinder?'
        subText="Your current Readiness Score and results will be replaced. You'll need to answer everything again."
        leftButtonLabel='Cancel'
        rightButtonLabel='Retake'
        onClickLeftBtn={() => setIsRetakeModalOpen(false)}
        onClickRightBtn={handleConfirmRetake}
        overlayClassName='bg-surface-black60 md:bg-surface-black60'
      />
    </div>
  );
}

// ─── 결과 공통 헤더 (배지 + 제목 + 설명) ────────────────────────────────────
function ResultHero({ subtitle, isBeta }) {
  return (
    <div className='flex w-full flex-col items-center gap-3 pb-[60px]'>
      <div className='flex items-center gap-2'>
        <span
          className={`${applyTypography('b12Strong')} rounded-[30px] bg-green-light01 px-2 py-1 text-center text-green-deep02`}
        >
          Assessment Complete
        </span>
        {isBeta && (
          <span
            className={`${applyTypography('b12Strong')} rounded-[30px] bg-grayscale-gray02 px-2 py-1 text-center text-text-secondary`}
          >
            Beta
          </span>
        )}
      </div>
      <p
        className={`${applyTypography('h6')} w-full text-center text-text-primary`}
      >
        Your Pathfinder Results
      </p>
      <p
        className={`${applyTypography('b16')} w-full text-center text-text-placeholder`}
      >
        {subtitle}
      </p>
    </div>
  );
}

const GAUGE_ANIM_MS = 900;

// 마운트 시 0 → target 까지 숫자가 올라가는 카운트업
function useCountUp(target, duration = GAUGE_ANIM_MS) {
  const end =
    target != null && Number.isFinite(Number(target)) ? Number(target) : null;
  const [value, setValue] = useState(end == null ? null : 0);

  useEffect(() => {
    if (end == null) {
      setValue(null);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(end * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);

  return value;
}

function SemiCircleGauge({ score }) {
  const numeric = score != null ? Number(score) : null;
  const hasScore = Number.isFinite(numeric);
  const pct = Math.max(0, Math.min(100, hasScore ? numeric : 0));

  const data = useMemo(() => [{ value: pct, color: '#1dc37b' }], [pct]);

  const animatedScore = useCountUp(hasScore ? numeric : null);
  const displayScore = animatedScore == null ? '–' : animatedScore.toFixed(1);

  return (
    <div className='relative w-full max-w-[373px]'>
      <div
        className='relative w-full overflow-hidden'
        style={{ aspectRatio: '200 / 112' }}
        role='img'
        aria-label={`Final score ${formatScore100(score)} out of 100`}
      >
        <div className='absolute inset-x-0 top-0 w-full aspect-square'>
          <PieChart
            data={data}
            totalValue={100}
            startAngle={180}
            lengthAngle={180}
            lineWidth={16}
            rounded
            background='#ece9e4'
            animate
            animationDuration={GAUGE_ANIM_MS}
          />
        </div>
      </div>
      <div className='absolute inset-0 flex flex-col items-center justify-center gap-2 pt-14'>
        <p className={`${applyTypography('h1')} text-center text-text-primary`}>
          {displayScore}
        </p>
        <p
          className={`${applyTypography('b14Strong')} text-center text-text-secondary`}
        >
          / 100
        </p>
      </div>
    </div>
  );
}

// Score Details 카드의 한 줄
function WeightedRow({ label, value, position }) {
  const shape =
    position === 'top'
      ? 'border p-6 rounded-tl-[16px] rounded-tr-[16px]'
      : 'border-b border-l border-r px-6 py-5 rounded-bl-[16px] rounded-br-[16px]';
  return (
    <div
      className={`flex w-full items-center justify-between border-solid border-grayscale-gray02 bg-grayscale-gray01 ${shape}`}
    >
      <div className='flex items-baseline gap-1 whitespace-nowrap'>
        <p className={`${applyTypography('b18Strong')} text-text-primary`}>
          {label}
        </p>
        <p className={`${applyTypography('b16')} text-text-tertiary`}>
          (weighted)
        </p>
      </div>
      <div className='flex items-baseline gap-1 whitespace-nowrap'>
        <p className={`${applyTypography('title1')} text-green-deep04`}>
          {formatScore100(value)}
        </p>
        <p className={`${applyTypography('b16Strong')} text-grayscale-gray05`}>
          / 100
        </p>
      </div>
    </div>
  );
}

function UserScoreView({ result, onRetake, isBeta }) {
  const { coreScore, overlayScore100, finalScore } = result;

  return (
    <div className='flex w-full flex-col items-center bg-grayscale-gray00 px-6 pb-[240px] pt-20 tabletH:px-20'>
      <div className='flex w-full max-w-[868px] flex-col items-center'>
        <ResultHero
          subtitle="Based on your responses, here's how we evaluated your profile."
          isBeta={isBeta}
        />

        {/* Final Score */}
        <div className='flex flex-col items-center w-full pb-20'>
          <div className='flex items-center justify-center w-full pb-6'>
            <p
              className={`${applyTypography('b12Strong')} text-text-secondary`}
            >
              Final Score
            </p>
          </div>
          <SemiCircleGauge score={finalScore} />
        </div>

        {/* Score Details */}
        <div className='flex flex-col items-start w-full pb-20'>
          <div className='flex flex-col items-center w-full px-2 pb-9'>
            <p
              className={`${applyTypography('title1')} w-full text-grayscale-gray09`}
            >
              Score Details
            </p>
          </div>
          <WeightedRow label='Core Scores' value={coreScore} position='top' />
          <WeightedRow
            label='Overlay Scores'
            value={overlayScore100}
            position='bottom'
          />
        </div>

        <ResultActions onRetake={onRetake} />
      </div>
    </div>
  );
}

// 어드민용 점수 결과 화면

function ScoreCard({ code, label, score }) {
  const value = score != null ? score : 0;
  return (
    <div className='flex flex-col items-end justify-center gap-6 rounded-[24px] bg-grayscale-gray01 p-6'>
      <div className='flex items-center w-full gap-3'>
        <div className='flex items-center justify-center rounded-full size-8 shrink-0 bg-grayscale-gray02'>
          <p className={`${applyTypography('b13Strong')} text-text-tertiary`}>
            {code}
          </p>
        </div>
        <p
          className={`${applyTypography('b16')} flex-1 truncate text-text-primary`}
        >
          {label}
        </p>
      </div>
      <div className='flex items-baseline gap-1 whitespace-nowrap'>
        <p className={`${applyTypography('h7')} text-green-deep03`}>{value}</p>
        <p className={`${applyTypography('b16')} text-text-secondary`}>/ 5</p>
      </div>
    </div>
  );
}

function TotalRow({ label, value }) {
  const display = formatScore100(value);
  return (
    <div className='flex items-center justify-between w-full px-6 py-5 border-t border-grayscale-gray03'>
      <div className='flex items-baseline gap-1 whitespace-nowrap'>
        <p className={`${applyTypography('b18Strong')} text-text-primary`}>
          {label}
        </p>
        <p className={`${applyTypography('b16')} text-text-tertiary`}>
          (weighted)
        </p>
      </div>
      <div className='flex items-baseline gap-1 whitespace-nowrap'>
        <p className={`${applyTypography('title1')} text-text-primary`}>
          {display}
        </p>
        <p className={`${applyTypography('b16Strong')} text-grayscale-gray05`}>
          / 100
        </p>
      </div>
    </div>
  );
}

function FinalScore({ score }) {
  const display = formatScore100(score);
  const pct = Math.max(0, Math.min(100, score != null ? Number(score) : 0));
  return (
    <div className='flex flex-col items-center w-full'>
      <div className='flex items-center justify-center w-full pb-6'>
        <p className={`${applyTypography('b12Strong')} text-text-secondary`}>
          Final Score
        </p>
      </div>
      <div className='flex w-full flex-col items-center gap-[60px]'>
        <div className='flex w-[120px] flex-col items-center gap-2 text-center'>
          <p className={`${applyTypography('h1')} w-full text-text-primary`}>
            {display}
          </p>
          <p className={`${applyTypography('b14')} w-full text-text-secondary`}>
            / 100
          </p>
        </div>
        <div className='relative w-full'>
          <div
            className='absolute z-10 -translate-x-1/2 -top-3'
            style={{ left: `${pct}%` }}
          >
            <div
              className='size-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-green-deep03'
              aria-hidden
            />
          </div>
          <div className='h-[26px] w-full overflow-hidden rounded-[30px] bg-grayscale-gray02'>
            <div
              className='h-full rounded-[30px] bg-green-deep02 transition-all duration-500'
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ScoreSection({ title, items, result, totalLabel, totalValue }) {
  return (
    <div className='flex flex-col items-start w-full pb-20'>
      <div className='flex flex-col items-center w-full px-2 pb-9'>
        <p
          className={`${applyTypography('title1')} w-full text-grayscale-gray09`}
        >
          {title}
        </p>
      </div>
      <div className='grid w-full grid-cols-1 gap-3 pb-9 mobile:grid-cols-2 tabletH:grid-cols-3'>
        {items.map((item) => (
          <ScoreCard
            key={item.code}
            code={item.code}
            label={item.label}
            score={result[item.key]}
          />
        ))}
      </div>
      <TotalRow label={totalLabel} value={totalValue} />
    </div>
  );
}

function AdminScoreView({ result, onRetake, isBeta }) {
  const { coreScore, overlayScore100, finalScore } = result;

  const finalScore100 = finalScore != null ? Number(finalScore) : null;
  const coreScore100 = coreScore != null ? Number(coreScore) : null;

  return (
    <div className='flex w-full flex-col items-center bg-grayscale-gray00 px-6 pb-[240px] pt-20 tabletH:px-20'>
      <div className='flex w-full max-w-[868px] flex-col items-start'>
        {/* Hero */}
        <div className='flex flex-col items-start w-full pb-20'>
          <ResultHero
            subtitle="Based on your responses, here's how we evaluated your profile. You can run another score check anytime."
            isBeta={isBeta}
          />
          <FinalScore score={finalScore100} />
        </div>

        {/* Core Scores */}
        <ScoreSection
          title='Core Scores'
          items={CORE_ITEMS}
          result={result}
          totalLabel='Core Total'
          totalValue={coreScore100}
        />

        {/* Overlay Scores */}
        <ScoreSection
          title='Overlay Scores'
          items={OVERLAY_ITEMS}
          result={result}
          totalLabel='Overlay Total'
          totalValue={overlayScore100}
        />

        <ResultActions onRetake={onRetake} />
      </div>
    </div>
  );
}

function PathfinderResult({ result, goalType, isAdmin = false, onRetake }) {
  const isBeta = Boolean(goalType && goalType !== 'JOB');

  if (isAdmin) {
    return (
      <AdminScoreView result={result} onRetake={onRetake} isBeta={isBeta} />
    );
  }
  return <UserScoreView result={result} onRetake={onRetake} isBeta={isBeta} />;
}

export default PathfinderResult;
